import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mail, Phone, CheckCircle, AlertCircle } from 'lucide-react';
import { FadeInLeft, FadeInRight } from './animations/AnimatedSection';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [focusedField, setFocusedField] = useState(null);

  const companyEmail = 'contact@limitless-solutions.pl';
  const companyPhone = '+48 600 037 207';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        message: formData.message
      });
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
      
      // Reset after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
      
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputVariants = {
    focused: { scale: 1.02, borderColor: 'rgba(201, 169, 98, 0.5)' },
    unfocused: { scale: 1, borderColor: 'rgba(201, 169, 98, 0.2)' },
  };

  const contactItems = [
    { icon: Mail, label: t('contact.email'), value: companyEmail, href: `mailto:${companyEmail}` },
    { icon: Phone, label: t('contact.phone'), value: companyPhone, href: `tel:${companyPhone.replace(/\s/g, '')}` },
  ];

  return (
    <section id="contact" className="py-32 bg-white relative overflow-hidden">
      {/* Animated background */}
      <motion.div
        className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-stone-50 to-transparent"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      />

      {/* Floating shapes */}
      <motion.div
        className="absolute top-20 right-20 w-32 h-32 border border-gold/10 rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
      />
      <motion.div
        className="absolute bottom-40 left-10 w-20 h-20 border border-gold/10 rounded-full"
        animate={{ rotate: -360 }}
        transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <FadeInLeft>
            <motion.span
              className="text-xs font-medium text-gold tracking-[0.3em] uppercase mb-4 block"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {t('contact.label')}
            </motion.span>
            
            <motion.h2
              className="font-serif text-4xl md:text-5xl text-navy font-light leading-tight mb-8 overflow-hidden"
            >
              <motion.span
                className="block"
                initial={{ y: 80 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              >
                {t('contact.title1')}
              </motion.span>
              <motion.span
                className="block"
                initial={{ y: 80 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
              >
                {t('contact.title2')}
              </motion.span>
            </motion.h2>
            
            <motion.p
              className="text-navy/60 font-light text-lg leading-relaxed mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {t('contact.description')}
            </motion.p>

            {/* Contact Details */}
            <div className="space-y-6">
              {contactItems.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                >
                  <motion.a
                    href={item.href}
                    className="flex items-center gap-4 group"
                    whileHover={{ x: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div
                      className="w-12 h-12 flex items-center justify-center rounded-xl bg-stone-50 group-hover:bg-gold/10 transition-colors duration-300"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <item.icon className="w-5 h-5 text-gold" />
                    </motion.div>
                    <div>
                      <div className="text-xs text-navy/50 font-light uppercase tracking-wider mb-1">
                        {item.label}
                      </div>
                      <div className="text-navy font-light group-hover:text-gold transition-colors duration-300">
                        {item.value}
                      </div>
                    </div>
                  </motion.a>
                </motion.div>
              ))}
            </div>
          </FadeInLeft>

          {/* Right - Contact Form */}
          <FadeInRight>
            <motion.div
              className="liquid-glass-card p-8 lg:p-10"
              whileHover={{ boxShadow: '0 25px 50px -12px rgba(201, 169, 98, 0.1)' }}
            >
              <AnimatePresence mode="wait">
                {submitStatus === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="h-full flex flex-col items-center justify-center text-center py-12"
                  >
                    <motion.div
                      className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mb-6"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200 }}
                    >
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </motion.div>
                    <motion.h3
                      className="font-serif text-2xl text-navy mb-3"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      {t('contact.form.success')}
                    </motion.h3>
                    <motion.p
                      className="text-navy/60 font-light"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      {t('contact.form.successMsg')}
                    </motion.p>
                  </motion.div>
                ) : submitStatus === 'error' ? (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="h-full flex flex-col items-center justify-center text-center py-12"
                  >
                    <motion.div
                      className="w-20 h-20 flex items-center justify-center rounded-full bg-red-100 mb-6"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200 }}
                    >
                      <AlertCircle className="w-10 h-10 text-red-600" />
                    </motion.div>
                    <motion.h3
                      className="font-serif text-2xl text-navy mb-3"
                    >
                      {t('contact.form.error')}
                    </motion.h3>
                    <motion.p className="text-navy/60 font-light mb-6">
                      {t('contact.form.errorMsg')}
                    </motion.p>
                    <button
                      onClick={() => setSubmitStatus(null)}
                      className="liquid-glass-button px-6 py-2"
                    >
                      {t('contact.form.retry')}
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0 }}
                      >
                        <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                          {t('contact.form.name')}
                        </label>
                        <motion.input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField(null)}
                          variants={inputVariants}
                          animate={focusedField === 'name' ? 'focused' : 'unfocused'}
                          className="w-full px-4 py-3 liquid-glass-input text-navy font-light placeholder:text-navy/30"
                          placeholder={t('contact.form.namePlaceholder')}
                        />
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                      >
                        <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                          {t('contact.form.company')}
                        </label>
                        <motion.input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('company')}
                          onBlur={() => setFocusedField(null)}
                          variants={inputVariants}
                          animate={focusedField === 'company' ? 'focused' : 'unfocused'}
                          className="w-full px-4 py-3 liquid-glass-input text-navy font-light placeholder:text-navy/30"
                          placeholder={t('contact.form.companyPlaceholder')}
                        />
                      </motion.div>
                    </div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                    >
                      <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                        {t('contact.form.emailLabel')}
                      </label>
                      <motion.input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        variants={inputVariants}
                        animate={focusedField === 'email' ? 'focused' : 'unfocused'}
                        className="w-full px-4 py-3 liquid-glass-input text-navy font-light placeholder:text-navy/30"
                        placeholder={t('contact.form.emailPlaceholder')}
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                    >
                      <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                        {t('contact.form.message')}
                      </label>
                      <motion.textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField(null)}
                        variants={inputVariants}
                        animate={focusedField === 'message' ? 'focused' : 'unfocused'}
                        className="w-full px-4 py-3 liquid-glass-input text-navy font-light placeholder:text-navy/30 resize-none"
                        placeholder={t('contact.form.messagePlaceholder')}
                      />
                    </motion.div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full liquid-glass-button px-8 py-4 font-light tracking-wide disabled:opacity-70 disabled:cursor-not-allowed"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 }}
                    >
                      <AnimatePresence mode="wait">
                        {isSubmitting ? (
                          <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center justify-center gap-3"
                          >
                            <motion.div
                              className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            />
                            {t('contact.form.sending')}
                          </motion.div>
                        ) : (
                          <motion.div
                            key="submit"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center justify-center gap-3"
                          >
                            {t('contact.form.submit')}
                            <Send className="w-4 h-4" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </FadeInRight>
        </div>
      </div>
    </section>
  );
};

export default Contact;
